import React from "react";
import Joi from "joi-browser";
import { toast } from "react-toastify";
import _ from "lodash";

import Media from "../../common/media";

import FormHeader from "../../common/form/formHeader";
import FormFooter from "../../common/form/formFooter";

import {
  getEntities,
  getEntity,
  saveEntity,
} from "../../../services/apiService";

class TourFrom extends Media {
  state = {
    data: {
      category_id: "",
      title_en: "",
      title_de: "",
      description_en: "",
      description_de: "",
      path: [],
      tags: [],
    },
    currentPath: [],
    categories: [],
    tags: [],
    pois: [],
    errors: {},
  };

  // Define the Form Schema
  schema = {
    _id: Joi.string(),
    category_id: Joi.string().required().label("Category"),
    title_en: Joi.string().min(3).max(1024).required().label("Title"),
    title_de: Joi.string().min(3).max(1024).required().label("Titel"),
    description_en: Joi.string()
      .min(5)
      .max(65536)
      .required()
      .label("Description"),
    description_de: Joi.string()
      .min(5)
      .max(65536)
      .required()
      .label("Beschreibung"),

    path: Joi.array().required().label("Path"),
    tags: Joi.array().required().label("Tags"),
  };

  // Retrive the Data from the Server
  async componentDidMount() {
    // Get Categories and Modify
    const { data: allCategories } = await getEntities("category");
    if (allCategories.length === 0) return;

    // When at least a Category exists in the database:
    const categories = allCategories.map(({ name_en: name, ...rest }) => ({
      name,
      ...rest,
    }));

    // Get Pois and Modify
    const { data: allPois } = await getEntities("poi");
    if (allPois.length === 0) return;

    // When at least a Poi exists in the database:
    const pois = allPois.map(({ title_en: name, ...rest }) => ({
      name,
      ...rest,
    }));

    // Get Tags and Modify
    const { data: allTags } = await getEntities("tag");

    // When at least a Tag exists in the database:
    const tags = allTags.map(({ title_en: name, ...rest }) => ({
      name,
      ...rest,
    }));

    if (allTags.length === 0) {
      this.setState({ pois, categories });
    } else {
      this.setState({ pois, categories, tags });
    }

    // Get the Given ID
    const tour_id = this.props.match.params.id;

    if (tour_id === "new") return;

    // Get Tour related to the Given ID
    const { data: tour } = await getEntity("tour", tour_id);
    if (!tour) return this.props.history.replace("/not-found");

    this.setState({
      data: this.mapToViewModel(tour),
      currentPath: [...tour.path],
    });
  }

  // Check if this tour has sections
  async componentDidUpdate(prevProps, prevState) {
    // If reorder path
    const { data, currentPath } = this.state;
    if (data.path.length === currentPath.length) return;

    // Only when editing a tour
    if (this.props.match.params.id === "new") return;

    // Get Sections and Modify
    const { data: allSections } = await getEntities("section");
    if (allSections.length === 0) return;

    // Find Sections related to this tour
    const tourSections = allSections.filter(
      (s) => s.tour_id === this.props.match.params.id
    );

    if (!_.isEqual(currentPath.sort(), data.path.sort())) {
      // Find the removed Poi ID form the path
      const removedPoi = currentPath.filter((e) => !data.path.includes(e));

      // Find the Section(s) that have this removed Poi
      const sections = tourSections.filter(
        (s) => s.target_id === removedPoi.toString()
      );

      if (sections.length) {
        toast.error(
          <div>
            Not allowed!
            <br /> First, Delete the Section Priorirty "
            {sections.length === 1
              ? sections[0].priority
              : sections.map((s) => s.priority).join(", ")}
            " of the Tour: "{data.title_en}".
          </div>
        );

        data["path"] = [...currentPath];
        this.setState({ data });
      }
    }
  }

  // Map current Tour to the Form Inputs
  mapToViewModel(tour) {
    return {
      _id: tour._id,
      category_id: tour.category_id,
      title_en: tour.title_en,
      title_de: tour.title_de,
      description_en: tour.description_en,
      description_de: tour.description_de,
      path: tour.path,
      tags: tour.tags,
    };
  }

  // Form submit Function
  doSubmit = async () => {
    // Tour to Save
    const data = { ...this.state.data };

    // Save Tour to the Database
    try {
      const response = await saveEntity("tour", data);
      if (response.data && response.status === 200) {
        toast.success(
          <div>
            Tour: "{response.data.title_en}" <br /> saved successfully.
          </div>
        );
      }
    } catch (ex) {
      toast.error(ex.response?.message ?? ex.response.data?.message);

      return;
    }

    // Return to Tours TableView after saving
    this.handleBack();
  };

  // Back button Event Handler
  handleBack = () => {
    // Table Pagination Query
    const searchParams = new URLSearchParams(window.location.search);
    const currentPageQuery = searchParams.get("currentPage");
    const pageSizeQuery = searchParams.get("pageSize");

    // Return to Tours TableView after saving
    if (currentPageQuery && pageSizeQuery) {
      this.props.history.push(
        `/dashboard/tours?pageSize=${pageSizeQuery}&currentPage=${currentPageQuery}`
      );
    } else {
      this.props.history.push("/dashboard/tours");
    }
  };

  render() {
    const id = this.props.match.params.id;
    const { pois, tags, categories, errors } = this.state;

    return (
      <div className="col alt-table-responsive">
        <FormHeader name="Tour" icon="bx bxs-been-here" id={id} />
        <form
          onSubmit={this.handleSubmit}
          className="form-group"
          encType="multipart/form-data"
        >
          <div className="row pt-2 mt-2">
            <div className=" col-sm-6">
              <div className="row align-items-center">
                <div className="col-sm-8">
                  {this.renderSelect("category_id", "Category", categories)}
                </div>
              </div>

              <div className="row mb-4 pb-2">
                <div className="col">
                  {this.renderMultiList(tags, "tags", "Tags", "Select Tags...")}
                </div>
              </div>

              <hr />

              <div className="row mt-4">
                <div className="col">
                  {this.renderInput("title_en", "Title")}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {this.renderTextBox("description_en", "Description")}
                </div>
              </div>

              <hr />

              <div className="row mt-4">
                <div className="col">
                  {this.renderInput("title_de", "Titel")}
                </div>
              </div>
              <div className="row">
                <div className="col">
                  {this.renderTextBox("description_de", "Beschreibung")}
                </div>
              </div>
            </div>
            <div className="col-sm-6 border-left">
              <div className="row pt-1">
                <div className="col mb-3">{this.renderTourMap()}</div>
              </div>

              <hr style={{ marginTop: "1.36rem" }} />

              <div className="row mt-4 ">
                <div className="col mb-2">
                  {this.renderDnDList(
                    pois,
                    "path",
                    "Histocache Options",
                    "Tour Path"
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col">{this.renderErrors(errors)}</div>
              </div>
            </div>
          </div>
          <FormFooter
            onBackClick={this.handleBack}
            onValidate={this.validate()}
          />
        </form>
      </div>
    );
  }
}

export default TourFrom;
